/**
 * Formate un montant en euros selon le format français complet.
 * Jamais d'abréviation K ou M. Toujours le montant réel.
 * Exemples: 3247 → "3 247 €", 35842.50 → "35 842,50 €"
 */
export const formatCurrency = (amount) => {
  if (amount == null || amount === '' || isNaN(Number(amount))) return '—';
  const num = Number(amount);
  const hasCents = Math.round(Math.abs(num) * 100) % 100 !== 0;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Formate une date ISO en format français JJ/MM/AAAA
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  } catch {
    return dateStr;
  }
};

/**
 * Retourne true si la date est dans le passé (retard)
 */
export const isLate = (dueDateStr) => {
  if (!dueDateStr) return false;
  return dueDateStr < new Date().toISOString().split('T')[0];
};

/**
 * Ajoute n mois à une date ISO, en gérant les fins de mois
 */
export const addMonths = (dateStr, n) => {
  if (!dateStr) return '';
  let [year, month, day] = dateStr.split('-').map(Number);
  month += n;
  while (month > 12) { month -= 12; year++; }
  while (month < 1) { month += 12; year--; }
  const maxDay = new Date(year, month, 0).getDate();
  day = Math.min(day, maxDay);
  return `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
};

/**
 * Calcule la valeur totale d'un contrat (initial + récurrents, SANS l'acompte)
 */
export const calcContractValue = (initial, hasRecurring, recurringAmount, recurringCount) => {
  const init = Number(initial) || 0;
  const recurring = hasRecurring ? (Number(recurringAmount) || 0) * (Number(recurringCount) || 0) : 0;
  return Math.round((init + recurring) * 100) / 100;
};

/**
 * Génère les lignes d'un échéancier côté frontend (pour prévisualisation)
 */
export const previewSchedule = (plan) => {
  const lines = [];
  const today = new Date().toISOString().split('T')[0];
  // Support both fixed and percent deposit types
  let rawDeposit = 0;
  if (plan.has_deposit) {
    if (plan.deposit_type === 'percent') {
      rawDeposit = Math.round((plan.initial_amount || 0) * (plan.deposit_amount || 0) / 100 * 100) / 100;
    } else {
      rawDeposit = plan.deposit_amount || 0;
    }
  }
  const depositCents = Math.round(rawDeposit * 100);

  if (plan.has_deposit && depositCents > 0) {
    lines.push({ type: 'deposit', num: 1, total: 1, amount: depositCents / 100, due_date: plan.deposit_due_date || today });
  }

  if (plan.has_initial && plan.initial_amount > 0) {
    const remaining = Math.round(plan.initial_amount * 100) - depositCents;
    if (remaining > 0) {
      const n = Math.max(1, plan.initial_installments || 1);
      const base = Math.floor(remaining / n);
      const extra = remaining % n;
      const start = plan.initial_start_date || today;
      for (let i = 1; i <= n; i++) {
        const cents = base + (i === n ? extra : 0);
        lines.push({ type: 'initial', num: i, total: n, amount: cents / 100, due_date: addMonths(start, i - 1) });
      }
    }
  }

  if (plan.has_recurring && plan.recurring_amount > 0) {
    const count = Math.max(1, plan.recurring_count || 1);
    const freq = plan.recurring_frequency || 'monthly';
    const freqMonths = { monthly: 1, quarterly: 3, annual: 12 }[freq] || 1;
    const start = plan.recurring_start_date || today;
    for (let i = 1; i <= count; i++) {
      lines.push({ type: 'recurring', num: i, total: count, amount: plan.recurring_amount, due_date: addMonths(start, (i - 1) * freqMonths) });
    }
  }

  return lines;
};
